// Side grouping for the paired (CRI | Insurer) grids.
//
// A paired row carries both legs of a match, so every column belongs to a side. The
// side is shown as a spanning banner ABOVE the column headers — the same device the
// Excel paired sheets use (reporting/export.py::_write_paired_spec writes a merged
// "CRI" / insurer-name banner over each band), so the two surfaces read alike.
//
// Spans are derived from the FINAL rendered column order, not assumed to be two
// blocks: applyPins hoists pinned columns to the front, which can interleave sides
// (pin a CRI column and an insurer column and the order becomes cri, ins, cri…).
// Scanning for runs keeps every banner truthful under any pin arrangement — the
// degenerate no-pin case collapses to exactly the two bands.

export type ColumnSide = "cri" | "ins" | "meta" | "carried";

export interface SideSpan {
  side: ColumnSide;
  label: string;
  span: number;
  /** Key of the run's first column — the span's React key and its sticky-offset anchor. */
  firstKey: string;
  /** Every column key in the run, so the banner can drive run-level interactions. */
  keys: string[];
}

/** Side of a composite paired column key ("cri::x" / "ins::x" / "meta::x" / "carried::x"). */
export function sideOf(key: string): ColumnSide {
  const i = key.indexOf("::");
  if (i < 0) return "meta";
  const p = key.slice(0, i);
  return p === "cri" || p === "ins" || p === "meta" || p === "carried" ? p : "meta";
}

/** Strip the composite prefix, giving the raw frame column name. */
export function rawKeyOf(key: string): string {
  const i = key.indexOf("::");
  return i < 0 ? key : key.slice(i + 2);
}

/**
 * Collapse an ordered column list into consecutive same-side runs.
 *
 * `insurerName` names the insurer band; it falls back to "Insurer" so an unresolved
 * name still reads as a side rather than as a blank banner.
 */
export function sideSpans(
  cols: { key: string }[],
  insurerName: string,
): SideSpan[] {
  // "carried" is the prior-period item's OWN band: it comes from finance's carried
  // file, not from either ledger, so it needs a third name — sharing the cri/ins
  // labels would claim the item was read off that ledger this period.
  const label = (s: ColumnSide): string =>
    s === "cri" ? "CRI" : s === "ins" ? (insurerName || "Insurer")
      : s === "carried" ? "Prior-period item" : "Match";

  const out: SideSpan[] = [];
  for (const c of cols) {
    const side = sideOf(c.key);
    const last = out[out.length - 1];
    if (last && last.side === side) {
      last.span += 1;
      last.keys.push(c.key);
    } else {
      out.push({ side, label: label(side), span: 1, firstKey: c.key, keys: [c.key] });
    }
  }
  return out;
}
